import * as React from 'react';

import type {CoreEntityType} from 'nutshell-core/types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Checkbox} from 'shells/checkbox/checkbox';
import {FormField} from 'shells/form/form-field';
// @ts-expect-error - imported from .js/.jsx file
import {AudienceIcon} from 'shells/icon/svg/audience-icon';
import {Popover, PopoverComponent} from 'shells/popover/popover';
import {PopoverForm} from 'shells/popover-form/popover-form';
import type {ListOptionWithCount} from 'shells/select-option-components/option-with-count';
import {Body} from 'shells/typography/body';

import {GraphQLSelectAudiences} from '../select/graphql-select-audiences';
import {GraphQLSelectAudiencesProvider} from '../select/graphql-select-audiences-provider';

import styles from './add-to-audience-popover.module.css';

type Props = {
    entityType: CoreEntityType;
    onCancel: () => void;
    onSubmit: (audience: ListOptionWithCount, onlyPrimaryContact: boolean) => Promise<any>;
    selectedCount: number;
} & JSX.LibraryManagedAttributes<
    typeof PopoverComponent,
    React.ComponentProps<typeof PopoverComponent>
> & {
        showConfirmationPage: boolean;
        // If only a single item, include the name and we can show that (Add Gabe
        // Revells to an audience)
        itemName?: string | null | undefined;
        excludedAudienceIds?: string[];
    };

AddToAudiencePopover.defaultProps = {
    showConfirmationPage: true,
};

export function AddToAudiencePopover(props: Props) {
    const [onlyPrimarySelected, setOnlyPrimarySelected] = React.useState<boolean>(false);
    const [selectedAudience, setAudience] = React.useState<ListOptionWithCount | null | undefined>(
        null
    );
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [isSubmitComplete, setIsSubmitComplete] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);

    // Storing this here so we can show it in the confirmation after submission,
    // we don't want our parent component to change this after success
    const [numberOfItemsSelected] = React.useState<number>(props.selectedCount);

    function handleSubmit() {
        if (!selectedAudience) return;

        if (isSubmitComplete) {
            // @ts-expect-error
            window.location = `/email-marketing/audiences/${selectedAudience.value}`;
        } else {
            setIsSubmitting(true);
            props
                .onSubmit(selectedAudience, onlyPrimarySelected)
                .then(() => {
                    if (props.showConfirmationPage) {
                        setIsSubmitComplete(true);
                    } else {
                        props.onCancel();
                    }
                    setIsSubmitting(false);
                })
                .catch((err) => {
                    // TODO: get an error from the server and show it.
                    setIsSubmitting(false);
                    if (err) {
                        setErrorMessage(safelyGetGraphQLErrorMessage(err));
                    } else {
                        setErrorMessage(
                            `Sorry, something went wrong.  Not everyone was added to the '${selectedAudience.label}' Audience`
                        );
                    }
                });
        }
    }

    if (errorMessage) {
        return (
            <Popover
                anchor={props.anchor}
                isDialog={true}
                onBlur={() => {
                    setErrorMessage(null);
                    props.onCancel();
                }}
                bodyColor='error'
            >
                {errorMessage}
            </Popover>
        );
    }

    const formContent = isSubmitComplete ? (
        <Body>
            {getSuccessText(
                numberOfItemsSelected,
                props.entityType,
                onlyPrimarySelected,
                selectedAudience
            )}
        </Body>
    ) : (
        <>
            {props.entityType === 'contacts' ? undefined : (
                <FormField innerId='primaryContacts'>
                    <Checkbox
                        // @ts-expect-error
                        name='primaryContacts'
                        label='Only add primary contacts'
                        isLarge={true}
                        checked={onlyPrimarySelected}
                        onChange={() => setOnlyPrimarySelected(!onlyPrimarySelected)}
                    />
                </FormField>
            )}
            <GraphQLSelectAudiencesProvider excludedAudienceIds={props.excludedAudienceIds}>
                {({options, isLoading}) => {
                    return (
                        <GraphQLSelectAudiences
                            options={options}
                            isLoading={isLoading}
                            isCreateable={true}
                            selectedId={selectedAudience ? selectedAudience.value : null}
                            // @ts-expect-error
                            shouldRefetch={isSubmitComplete}
                            onChange={(audience: ListOptionWithCount) => {
                                setAudience(audience);
                            }}
                            menuPlacement='auto'
                        />
                    );
                }}
            </GraphQLSelectAudiencesProvider>
        </>
    );

    return (
        <Popover anchor={props.anchor} onBlur={props.onCancel} location={props.location}>
            <PopoverForm
                width={340}
                allowTitleWrapping={true}
                onClose={props.onCancel}
                title={
                    <div className={styles['logo-title']}>
                        <AudienceIcon wrapWithDiv={false} size={25} />
                        {isSubmitComplete
                            ? 'Success!'
                            : getTitleText(
                                  numberOfItemsSelected,
                                  props.entityType,
                                  onlyPrimarySelected,
                                  props.itemName
                              )}
                    </div>
                }
                saveButtonDisabled={!selectedAudience || selectedAudience.__isNew__}
                isSaving={isSubmitting}
                saveButtonSavingText='Adding'
                saveButtonSuccessText='Added!'
                onSubmit={handleSubmit}
                saveButtonText={isSubmitComplete ? 'View audience' : 'Add to audience'}
                cancelButtonText={isSubmitComplete ? 'Close' : 'Cancel'}
                resetOnSaveComplete={true}
                saveCompleteTimeout={1000}
            >
                <div className={styles['form-container']}>{formContent}</div>
            </PopoverForm>
        </Popover>
    );
}

function getTitleText(
    numberOfItemsSelected: number,
    entityType: CoreEntityType,
    onlyPrimarySelected: boolean,
    name?: string | null
) {
    if (entityType === 'leads') {
        return (
            <div>
                Add {onlyPrimarySelected ? 'primary contacts' : 'people'} from{' '}
                {numberOfItemsSelected === 1 ? '1 lead' : `${numberOfItemsSelected} leads`} to an
                audience
            </div>
        );
    } else if (entityType === 'accounts') {
        return (
            <div>
                Add {onlyPrimarySelected ? 'primary contacts' : 'people'} from{' '}
                {numberOfItemsSelected === 1 ? '1 company' : `${numberOfItemsSelected} companies`}{' '}
                to an audience
            </div>
        );
    } else if (name) {
        return <div>Add {name} to an audience</div>;
    } else {
        return (
            <div>
                Add {numberOfItemsSelected === 1 ? '1 person' : `${numberOfItemsSelected} people`}{' '}
                to an audience
            </div>
        );
    }
}

function getSuccessText(
    numberOfItemsSelected: number,
    entityType: CoreEntityType,
    onlyPrimarySelected: boolean,
    selectedAudience?: ListOptionWithCount | null
) {
    if (entityType === 'leads') {
        return (
            <div>
                People from{' '}
                {numberOfItemsSelected === 1 ? '1 lead' : `${numberOfItemsSelected} leads`} were
                added
                {selectedAudience ? (
                    <span>
                        {' '}
                        to <strong>{selectedAudience.label}</strong>
                    </span>
                ) : undefined}
                . Your changes might take a few minutes to appear!
            </div>
        );
    } else if (entityType === 'accounts') {
        return (
            <div>
                People from{' '}
                {numberOfItemsSelected === 1 ? '1 company' : `${numberOfItemsSelected} companies`}{' '}
                were added
                {selectedAudience ? (
                    <span>
                        {' '}
                        to <strong>{selectedAudience.label}</strong>
                    </span>
                ) : undefined}
                . Your changes might take a few minutes to appear!
            </div>
        );
    } else {
        return (
            <div>
                {numberOfItemsSelected} {numberOfItemsSelected === 1 ? 'person was' : 'people were'}{' '}
                added
                {selectedAudience ? (
                    <span>
                        {' '}
                        to <strong>{selectedAudience.label}</strong>
                    </span>
                ) : undefined}
                . Your changes might take a few minutes to appear!
            </div>
        );
    }
}
